import { useEffect, useState, type ComponentType } from 'react';
import type { StoryHarnessRootProps } from '../dev/story-harness.js';

/**
 * Dev-only screen that mounts the story-harness registry.
 *
 * Dynamically imports `src/dev/story-harness.tsx` so the story modules
 * (loaded via `import.meta.glob('/src/stories/*.stories.{ts,tsx}', { eager: true })`)
 * are only pulled into the dev bundle. In production, this screen is a no-op.
 *
 * The storyId (e.g. `app-stories-button--primary`) is parsed from
 * `window.location.pathname` because the state-machine architecture in
 * `App.tsx` doesn't carry route params via the `parseScreen` helper. The path
 * is normalized to strip the `/__stories__/` prefix and any trailing slashes,
 * and resolves to `undefined` when empty so `StoryHarnessRoot` falls back to
 * the first registry entry.
 *
 * The prop is `initialStoryId`, and until 2026-08-24 this screen passed
 * `storyId` instead — a name `StoryHarnessRoot` does not read. The mismatch
 * survived because the dynamic import was cast to
 * `ComponentType<{ storyId: string }>`, which told TypeScript to stop
 * checking exactly the thing that was wrong. Every URL therefore rendered the
 * registry's first entry: `/__stories__/app-stories-mascot--default` and
 * `/__stories__/app-stories-coachpanel--idle` both showed
 * `app-stories-button--disabled`, and the toolbar's story picker — which works
 * by navigating to `/__stories__/<id>` — could not change the story either.
 * The cast is gone; the prop type now comes from the module itself.
 *
 * Both rendered branches carry `data-appdom-scope="story-harness"` so the
 * `audit:appdom` gate's "every screen declares its scope" invariant holds
 * here too, despite the real render being delegated to the dev harness root.
 */
export function StoryHarness() {
  const pathStoryId = decodeURIComponent(
    window.location.pathname.replace(/^\/__stories__\/?/, '').replace(/\/$/, ''),
  );
  const storyId = pathStoryId === '' ? undefined : pathStoryId;

  const [Root, setRoot] = useState<ComponentType<StoryHarnessRootProps> | null>(
    null,
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    import('../dev/story-harness.js').then((m) => {
      if (!cancelled) setRoot(() => m.StoryHarnessRoot);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!import.meta.env.DEV) return null;
  if (!Root) {
    return (
      <main
        className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center gap-3 p-6 text-center"
        data-appdom-scope="story-harness"
        data-appdom-id="story-harness-loading"
        data-appdom-type="Screen"
      >
        <p className="text-sm text-ink/70 dark:text-cream/70">
          Loading story harness…
        </p>
      </main>
    );
  }
  // `display: contents` — the wrapper carries the screen's app-dom scope
  // without introducing a box, so the harness lays out exactly as before and
  // the inner [data-appdom-scope="story"] mount the stories-in-app flow asserts
  // on is untouched. Every screen declares its scope; this one delegates its
  // render, so it needs somewhere to put it.
  return (
    <div data-appdom-scope="story-harness" className="contents">
      <Root initialStoryId={storyId} />
    </div>
  );
}
